import React, { useCallback, useEffect, useRef, useState } from "react";
import * as S from "./AdminBuildingDetail.styled";
import { adminBuildingService, BuildingCreateRequest, OperationTimeInput } from "services/adminBuildingService";
import { facilityService, BuildingFacility } from "services/facilityService";
import { Building } from "types";
import TimeSelect from "components/admin/TimeSelect";

interface Props {
  buildingId: number;
  onClose: () => void;
  onUpdated?: () => void;
}

interface FormValues {
  name: string;
  label: string;
  latitude: string;
  longitude: string;
  thumbnailImage: string;
  images: string[];
  operationTimes: OperationTimeInput[];
}

/** "HH:MM:SS" → "HH:MM" */
const toHHMM = (s: string) => (s ? s.slice(0, 5) : "");
/** "HH:MM" → "HH:MM:SS" */
const toHHMMSS = (s: string) => (s ? `${s}:00` : "");

const makeDefaultForm = (b: Building): FormValues => ({
  name: b.name ?? "",
  label: b.label ?? "",
  latitude: String(b.latitude ?? ""),
  longitude: String(b.longitude ?? ""),
  thumbnailImage: b.thumbnailImage ?? "",
  images: b.images ?? [],
  operationTimes: (b.operationTimes ?? []).map((t) => ({
    name: t.name,
    startTime: toHHMM(t.startTime),
    endTime: toHHMM(t.endTime),
  })),
});

const emptyOpTime = (): OperationTimeInput => ({ name: "", startTime: "", endTime: "" });

const AdminBuildingDetail: React.FC<Props> = ({ buildingId, onClose, onUpdated }) => {
  const [building, setBuilding] = useState<Building | null>(null);
  const [facilities, setFacilities] = useState<BuildingFacility[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  const [isEditing, setIsEditing] = useState(false);
  const [form, setForm] = useState<FormValues | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [uploadingThumb, setUploadingThumb] = useState(false);
  const [uploadingImg, setUploadingImg] = useState(false);

  const thumbInputRef = useRef<HTMLInputElement>(null);
  const imgInputRef = useRef<HTMLInputElement>(null);

  const fetchData = useCallback(() => {
    setIsLoading(true);
    Promise.all([
      adminBuildingService.getBuilding(buildingId),
      facilityService.getBuildingFacilities(buildingId),
    ])
      .then(([buildingData, facilityData]) => {
        setBuilding(buildingData);
        setSelectedImage(buildingData.thumbnailImage ?? null);
        setFacilities(facilityData);
      })
      .catch(() => {})
      .finally(() => setIsLoading(false));
  }, [buildingId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  /* ── 수정 모드 진입 ── */
  const handleEditStart = () => {
    if (!building) return;
    setForm(makeDefaultForm(building));
    setSaveError("");
    setIsEditing(true);
  };

  const handleEditCancel = () => {
    setIsEditing(false);
    setForm(null);
    setSaveError("");
  };

  /* ── 기본 필드 변경 ── */
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setForm((prev) => prev ? { ...prev, [name]: value } : prev);
  };

  /* ── 썸네일 업로드 ── */
  const handleThumbUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !form) return;
    setUploadingThumb(true);
    try {
      const url = await adminBuildingService.uploadImage(file);
      setForm((prev) => prev ? { ...prev, thumbnailImage: url } : prev);
    } catch {
      setSaveError("썸네일 업로드에 실패했습니다.");
    } finally {
      setUploadingThumb(false);
      e.target.value = "";
    }
  };

  /* ── 추가 이미지 업로드 ── */
  const handleImgUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (!files.length || !form) return;
    setUploadingImg(true);
    try {
      const urls = await Promise.all(files.map((f) => adminBuildingService.uploadImage(f)));
      setForm((prev) => prev ? { ...prev, images: [...prev.images, ...urls] } : prev);
    } catch {
      setSaveError("이미지 업로드에 실패했습니다.");
    } finally {
      setUploadingImg(false);
      e.target.value = "";
    }
  };

  const removeImage = (index: number) => {
    setForm((prev) => prev ? { ...prev, images: prev.images.filter((_, i) => i !== index) } : prev);
  };

  /* ── 운영 시간 ── */
  const handleOpTimeChange = (index: number, field: keyof OperationTimeInput, value: string) => {
    setForm((prev) => {
      if (!prev) return prev;
      const next = prev.operationTimes.map((row, i) =>
        i === index ? { ...row, [field]: value } : row
      );
      return { ...prev, operationTimes: next };
    });
  };

  const addOpTime = () => {
    setForm((prev) => prev ? { ...prev, operationTimes: [...prev.operationTimes, emptyOpTime()] } : prev);
  };

  const removeOpTime = (index: number) => {
    setForm((prev) => {
      if (!prev) return prev;
      return { ...prev, operationTimes: prev.operationTimes.filter((_, i) => i !== index) };
    });
  };

  /* ── 저장 ── */
  const handleSave = async () => {
    if (!form) return;
    setIsSaving(true);
    setSaveError("");
    try {
      const payload: BuildingCreateRequest = {
        name: form.name.trim(),
        label: form.label.trim(),
        latitude: Number(form.latitude),
        longitude: Number(form.longitude),
        thumbnailImage: form.thumbnailImage,
        images: form.images,
        operationTimes: form.operationTimes.map((t) => ({
          name: t.name.trim(),
          startTime: toHHMMSS(t.startTime),
          endTime: toHHMMSS(t.endTime),
        })),
      };
      await adminBuildingService.updateBuilding(buildingId, payload);
      setIsEditing(false);
      setForm(null);
      fetchData();
      onUpdated?.();
    } catch {
      setSaveError("저장에 실패했습니다. 다시 시도해주세요.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <S.Overlay onClick={onClose}>
      <S.Panel onClick={(e) => e.stopPropagation()}>

        {/* 헤더 */}
        <S.PanelHeader>
          <S.PanelTitle>{isEditing ? "건물 수정" : "건물 상세정보"}</S.PanelTitle>
          <S.HeaderActions>
            {!isEditing && building && (
              <S.EditButton onClick={handleEditStart}>수정</S.EditButton>
            )}
            <S.CloseButton onClick={onClose}>✕</S.CloseButton>
          </S.HeaderActions>
        </S.PanelHeader>

        {/* 바디 */}
        <S.PanelBody>
          {isLoading ? (
            <S.LoadingWrap>
              <S.LoadingText>불러오는 중...</S.LoadingText>
            </S.LoadingWrap>
          ) : !building ? (
            <S.LoadingWrap>
              <S.LoadingText>정보를 불러올 수 없습니다.</S.LoadingText>
            </S.LoadingWrap>
          ) : isEditing && form ? (
            /* ══════════════ 수정 폼 ══════════════ */
            <>
              {/* 건물명 */}
              <S.FormGroup>
                <S.FormLabel>건물명</S.FormLabel>
                <S.FormInput
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  placeholder="건물명"
                />
              </S.FormGroup>

              {/* 약칭 */}
              <S.FormGroup>
                <S.FormLabel>약칭</S.FormLabel>
                <S.FormInput
                  name="label"
                  value={form.label}
                  onChange={handleChange}
                  placeholder="예) 07"
                />
              </S.FormGroup>

              {/* 위도 / 경도 */}
              <S.FormRow>
                <S.FormGroup>
                  <S.FormLabel>위도</S.FormLabel>
                  <S.FormInput
                    name="latitude"
                    type="number"
                    step="any"
                    value={form.latitude}
                    onChange={handleChange}
                    placeholder="예) 37.123456"
                  />
                </S.FormGroup>
                <S.FormGroup>
                  <S.FormLabel>경도</S.FormLabel>
                  <S.FormInput
                    name="longitude"
                    type="number"
                    step="any"
                    value={form.longitude}
                    onChange={handleChange}
                    placeholder="예) 127.123456"
                  />
                </S.FormGroup>
              </S.FormRow>

              {/* 썸네일 이미지 */}
              <S.FormGroup>
                <S.FormLabel>썸네일 이미지</S.FormLabel>
                <S.ThumbUploadWrap
                  hasImage={!!form.thumbnailImage}
                  onClick={() => thumbInputRef.current?.click()}
                >
                  {form.thumbnailImage ? (
                    <>
                      <S.ThumbUploadImg src={form.thumbnailImage} alt="썸네일" />
                      <S.ThumbUploadOverlay>
                        {uploadingThumb ? "업로드 중..." : "클릭해서 변경"}
                      </S.ThumbUploadOverlay>
                    </>
                  ) : (
                    <S.ThumbUploadPlaceholder>
                      <span style={{ fontSize: 28 }}>📷</span>
                      <span>{uploadingThumb ? "업로드 중..." : "클릭해서 이미지 선택"}</span>
                    </S.ThumbUploadPlaceholder>
                  )}
                </S.ThumbUploadWrap>
                <input
                  ref={thumbInputRef}
                  type="file"
                  accept="image/*"
                  style={{ display: "none" }}
                  onChange={handleThumbUpload}
                />
              </S.FormGroup>

              {/* 추가 이미지 */}
              <S.FormGroup>
                <S.FormLabel>추가 이미지</S.FormLabel>
                <S.ImgGrid>
                  {form.images.map((url, i) => (
                    <S.ImgGridItem key={i}>
                      <S.ImgGridImg src={url} alt={`이미지 ${i + 1}`} />
                      <S.ImgGridRemove onClick={() => removeImage(i)}>✕</S.ImgGridRemove>
                    </S.ImgGridItem>
                  ))}
                  {uploadingImg && <S.ImgGridLoading>업로드 중...</S.ImgGridLoading>}
                  <S.ImgAddBtn
                    onClick={() => imgInputRef.current?.click()}
                    disabled={uploadingImg}
                  >
                    +
                  </S.ImgAddBtn>
                </S.ImgGrid>
                <input
                  ref={imgInputRef}
                  type="file"
                  accept="image/*"
                  multiple
                  style={{ display: "none" }}
                  onChange={handleImgUpload}
                />
              </S.FormGroup>

              {/* 운영 시간 */}
              <S.FormGroup>
                <S.FormLabel>운영 시간</S.FormLabel>
                {form.operationTimes.map((row, i) => (
                  <S.ArrayRow key={i} style={{ marginBottom: 8 }}>
                    <S.FormInput
                      placeholder="구분명 (예: 평일)"
                      value={row.name}
                      onChange={(e) => handleOpTimeChange(i, "name", e.target.value)}
                      style={{ flex: 1 }}
                    />
                    <TimeSelect
                      value={row.startTime}
                      onChange={(v) => handleOpTimeChange(i, "startTime", v)}
                      height={36}
                    />
                    <TimeSelect
                      value={row.endTime}
                      onChange={(v) => handleOpTimeChange(i, "endTime", v)}
                      height={36}
                    />
                    <S.RemoveButton
                      onClick={() => removeOpTime(i)}
                      disabled={form.operationTimes.length === 1}
                    >
                      ✕
                    </S.RemoveButton>
                  </S.ArrayRow>
                ))}
                <S.AddButton onClick={addOpTime}>+ 운영 시간 추가</S.AddButton>
              </S.FormGroup>

              {saveError && <S.SubmitError>{saveError}</S.SubmitError>}

              {/* 저장 / 취소 */}
              <S.FormActions>
                <S.CancelButton onClick={handleEditCancel} disabled={isSaving}>
                  취소
                </S.CancelButton>
                <S.SaveButton onClick={handleSave} disabled={isSaving || uploadingThumb || uploadingImg}>
                  {isSaving ? "저장 중..." : "저장"}
                </S.SaveButton>
              </S.FormActions>
            </>
          ) : (
            /* ══════════════ 조회 뷰 ══════════════ */
            <>
              {/* 이미지 뷰어 */}
              <S.ImageViewer>
                {selectedImage ? (
                  <S.MainImage src={selectedImage} alt={building.name} />
                ) : (
                  <S.ImagePlaceholder>🏢</S.ImagePlaceholder>
                )}
              </S.ImageViewer>

              {/* 썸네일 + 추가 이미지 목록 */}
              {(building.images?.length ?? 0) > 0 && (
                <S.ImageThumbs>
                  {building.thumbnailImage && (
                    <S.ThumbItem
                      src={building.thumbnailImage}
                      alt="썸네일"
                      isActive={selectedImage === building.thumbnailImage}
                      onClick={() => setSelectedImage(building.thumbnailImage)}
                    />
                  )}
                  {building.images?.map((img, i) => (
                    <S.ThumbItem
                      key={i}
                      src={img}
                      alt={`이미지 ${i + 1}`}
                      isActive={selectedImage === img}
                      onClick={() => setSelectedImage(img)}
                    />
                  ))}
                </S.ImageThumbs>
              )}

              {/* 기본 정보 */}
              <S.Section>
                <S.SectionTitle>기본 정보</S.SectionTitle>
                <S.InfoGrid>
                  <S.InfoRow>
                    <S.InfoLabel>건물명</S.InfoLabel>
                    <S.InfoValue>{building.name}</S.InfoValue>
                  </S.InfoRow>
                  <S.InfoRow>
                    <S.InfoLabel>약칭</S.InfoLabel>
                    <S.InfoValue>
                      <S.LabelBadge>{building.label}</S.LabelBadge>
                    </S.InfoValue>
                  </S.InfoRow>
                  <S.InfoRow>
                    <S.InfoLabel>위도</S.InfoLabel>
                    <S.InfoValue>
                      <S.CoordText>{building.latitude.toFixed(6)}</S.CoordText>
                    </S.InfoValue>
                  </S.InfoRow>
                  <S.InfoRow>
                    <S.InfoLabel>경도</S.InfoLabel>
                    <S.InfoValue>
                      <S.CoordText>{building.longitude.toFixed(6)}</S.CoordText>
                    </S.InfoValue>
                  </S.InfoRow>
                </S.InfoGrid>
              </S.Section>

              {/* 운영 시간 */}
              {building.operationTimes && building.operationTimes.length > 0 && (
                <S.Section>
                  <S.SectionTitle>운영 시간</S.SectionTitle>
                  <S.OpTimeTable>
                    <S.OpTimeHead>
                      <tr>
                        <S.OpTh>구분</S.OpTh>
                        <S.OpTh>시작</S.OpTh>
                        <S.OpTh>종료</S.OpTh>
                      </tr>
                    </S.OpTimeHead>
                    <tbody>
                      {building.operationTimes.map((t, i) => (
                        <S.OpTr key={i}>
                          <S.OpTd>{t.name}</S.OpTd>
                          <S.OpTd>{t.startTime.slice(0, 5)}</S.OpTd>
                          <S.OpTd>{t.endTime.slice(0, 5)}</S.OpTd>
                        </S.OpTr>
                      ))}
                    </tbody>
                  </S.OpTimeTable>
                </S.Section>
              )}

              {/* 시설 목록 */}
              <S.Section>
                <S.SectionTitle>시설 목록</S.SectionTitle>

                {facilities.length === 0 ? (
                  <S.FacilityEmpty>배정된 시설이 없습니다.</S.FacilityEmpty>
                ) : (
                  <S.FacilityList>
                    {facilities.map((f) => (
                      <S.FacilityCard key={f.id}>
                        {f.thumbnailImage ? (
                          <S.FacilityThumb src={f.thumbnailImage} alt={f.name} />
                        ) : (
                          <S.FacilityThumbPlaceholder>🏛️</S.FacilityThumbPlaceholder>
                        )}
                        <S.FacilityInfo>
                          <S.FacilityName>{f.name}</S.FacilityName>
                          <S.FacilityMeta>{f.location || f.purpose}</S.FacilityMeta>
                        </S.FacilityInfo>
                      </S.FacilityCard>
                    ))}
                  </S.FacilityList>
                )}
              </S.Section>
            </>
          )}
        </S.PanelBody>

      </S.Panel>
    </S.Overlay>
  );
};

export default AdminBuildingDetail;
